<template>
    <cic-popup :saveable="true" class="no-padding" title="Unassign Licences" @blur="$emit('blur')" @input="handleApply">
        <div class="item-form" style="padding: 10px 10px 10px 26px">
            <h3>{{ $t('Licences') }}</h3>
        </div>
        <table id="tblUnassignLicence" class="cic-table">
            <thead>
                <tr>
                    <th>{{ $t('Licence') }}</th>
                    <th>{{ $t('Dependent Licence') }}</th>
                    <th style="width: 90px">{{ $t('Unassign') }}</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>{{ $t('[All]') }}</td>
                    <td>-</td>
                    <td>
                        <cic-checkbox
                            :value="selectedLicences.length === quantities.length"
                            @input="(val) => handleLicenceClick(-1, val)"
                        />
                    </td>
                </tr>
                <template v-for="group in groups">
                    <tr>
                        <td class="primary" colspan="3">{{ group }}</td>
                    </tr>
                    <tr v-for="quantity in quantities.filter((x) => x.group === group)">
                        <td>{{ quantity.name }}</td>
                        <td>
                            <span v-if="quantity.required.length === 0">-</span>
                            <ul v-else>
                                <li
                                    v-for="required in quantities.filter((x) =>
                                        quantity.required.includes(x.quantityId),
                                    )"
                                >
                                    {{ required.name }}
                                </li>
                            </ul>
                        </td>
                        <td>
                            <cic-checkbox
                                :value="selectedLicences.includes(quantity.quantityId)"
                                @input="(val) => handleLicenceClick(quantity.quantityId, val)"
                            />
                        </td>
                    </tr>
                </template>
            </tbody>
        </table>
        <div class="item-form" style="padding: 10px 10px 10px 26px">
            <h3>{{ $t('Users') }}</h3>
        </div>
        <table id="tblUserUnassign" class="cic-table">
            <thead>
                <tr>
                    <th>{{ $t('User') }}</th>
                    <th style="width: 90px">{{ $t('Unassign') }}</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>{{ $t('[All]') }}</td>
                    <td>
                        <cic-checkbox
                            :value="selectedUsers.length === users.length"
                            @input="(val) => handleUserClick(-1, val)"
                        />
                    </td>
                </tr>
                <template v-for="user in users">
                    <tr>
                        <td>{{ formatUsername(user) }}</td>
                        <td>
                            <cic-checkbox
                                :value="selectedUsers.includes(user.userId)"
                                @input="(val) => handleUserClick(user.userId, val)"
                            />
                        </td>
                    </tr>
                </template>
            </tbody>
        </table>
    </cic-popup>
</template>

<script>
    import CicPopup from '@/components/CicPopup';
    import CicCheckbox from '@/components/inputs/CicCheckbox';
    import { load } from '@/utils/Http';
    import { deselect, formatUsername, getBlocked, getGroups, getUserLicences, select } from '../LicenceUtils';

    export default {
        name: 'UnassignLicences',
        components: {
            CicCheckbox,
            CicPopup,
        },
        props: {
            gracePeriod: {
                type: Boolean,
                default: false,
            },
            licences: {
                type: Array,
                required: true,
            },
            quantities: {
                type: Array,
                required: true,
            },
            users: {
                type: Array,
                required: true,
            },
        },
        emits: ['blur', 'input'],
        data() {
            return {
                groups: [],
                selectedLicences: [],
                selectedUsers: [],
            };
        },
        created() {
            this.groups = getGroups(this.quantities);
        },
        methods: {
            formatUsername,
            handleApply() {
                const users = this.users.filter((x) => this.selectedUsers.includes(x.userId));

                this.loadAssigned(users.filter((x) => !x.licences));
            },

            handleLicenceClick(quantityId, val) {
                if (val) {
                    if (quantityId === -1) {
                        this.selectedLicences = this.quantities.map((x) => x.quantityId);
                    } else {
                        this.selectedLicences = select(
                            this.quantities,
                            this.selectedLicences,
                            quantityId,
                            this.$t('TEMPLATE_SELECT_CHILDREN'),
                            false,
                        );
                    }
                } else {
                    if (quantityId === -1) {
                        this.selectedLicences = [];
                    } else {
                        this.selectedLicences = deselect(
                            this.quantities,
                            this.selectedLicences,
                            quantityId,
                            this.$t('TEMPLATE_DESELECT_PARENTS'),
                            false,
                        );
                    }
                }
            },

            handleUserClick(userId, val) {
                if (val) {
                    if (userId === -1) {
                        this.selectedUsers = this.users.map((x) => x.userId);
                    } else {
                        this.selectedUsers.push(userId);
                    }
                } else {
                    if (userId === -1) {
                        this.selectedUsers = [];
                    } else {
                        this.selectedUsers.splice(this.selectedUsers.indexOf(userId), 1);
                    }
                }
            },

            loadAssigned(users) {
                let guids = [];
                users.forEach((x) => {
                    guids = guids.concat(this.licences.filter((y) => y.assigned.includes(x.userId)).map((y) => y.id));
                });
                if (guids.length > 0) {
                    load('licencesummary/users', { guids: JSON.stringify(guids) }, (response) => {
                        users.forEach((x) => {
                            x.licences = response.users
                                .filter((y) => y.userId === x.userId)
                                .sort((a, b) => (!a.assigned || a.assigned > b.assigned ? 1 : -1));
                        });
                        this.validate();
                    });
                } else {
                    this.validate();
                }
            },

            validate() {
                const users = this.users.filter((x) => this.selectedUsers.includes(x.userId));
                const quantities = this.quantities.filter((x) => this.selectedLicences.includes(x.quantityId));

                let alerts = [];
                if (!this.gracePeriod) {
                    users.forEach((x) => {
                        const blocked = quantities
                            .filter((y) =>
                                getBlocked(
                                    this.quantities,
                                    this.gracePeriod,
                                    getUserLicences(x, this.licences),
                                    false,
                                ).includes(y.quantityId),
                            )
                            .map((y) =>
                                this.$t('[y] was assigned a [x] licence less than [z] days ago.')
                                    .replace(/\[x]/g, y.name)
                                    .replace(/\[y]/g, x.username)
                                    .replace(/\[z]/g, y.minimumPeriod),
                            );
                        alerts = alerts.concat(blocked);
                    });
                }

                if (
                    alerts.length === 0 ||
                    confirm(this.$t('UNASSIGN_LICENCE_CONFIRM').replace(/\[x]/g, '\n' + alerts.join('\n')))
                ) {
                    const removals = [];
                    users.forEach((x) => {
                        const userLicences = getUserLicences(x, this.licences);
                        const blocked = getBlocked(this.quantities, this.gracePeriod, userLicences);
                        const guids = userLicences
                            .filter(
                                (y) => this.selectedLicences.includes(y.quantityId) && !blocked.includes(y.quantityId),
                            )
                            .map((y) => y.guid);
                        if (guids.length > 0) {
                            removals.push({ userId: x.userId, ids: guids });
                        }
                    });

                    this.$emit('input', removals);
                }
            },
        },
    };
</script>
